import Service, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from 'ember-concurrency';
import { query } from '@warp-drive/utilities/json-api';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-common/services/session-context';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import { REPORT_TYPE_FILTER_VALUE } from '../utils/dataset-utils.ts';
import { updateReportRequest } from '../requests/report.ts';
import { visibleToAdmin, companyIdFor } from '../utils/report-filtering.ts';

export interface CompanyOption {
  key: string;
  label: string;
}

/**
 * One page of reports; the API's default sort is updated_at desc, so these
 * are the most recently updated. mayHaveMore flags a full page so the UI
 * can say counts/search are bounded.
 */
export const REPORTS_PAGE_LIMIT = 200;

/**
 * One shared fetch of the admin reports catalog (reports + active companies)
 * for the Reports Home and Browse pages, so navigating between them doesn't
 * refetch. Archive/publish flows mutate report records in place, which keeps
 * this cached array coherent without a refresh.
 */
export default class ReportsCatalogService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked reports: Report[] = [];
  @tracked companies: CompanyOption[] = [];
  /** True when the initial load failed; pages surface a retry hint. */
  @tracked loadFailed = false;

  @tracked private loaded = false;
  /** Companies change rarely; fetched once per session (refresh refetches). */
  private companiesLoaded = false;
  /** Owner the cached catalog belongs to — the singleton survives re-login
   * without app teardown, so an owner change drops the cache. */
  private ownerForLoad: string | null = null;

  get isLoading() {
    return !this.loaded && this.load.isRunning;
  }

  get hasLoaded() {
    return this.loaded;
  }

  /** Reports the signed-in admin may see (type + private-ownership enforced). */
  @cached
  get visibleReports() {
    return visibleToAdmin(this.reports, this.sessionContext.user?.id);
  }

  @cached
  get companyNamesById() {
    return new Map(this.companies.map((c) => [c.key, c.label]));
  }

  /** True when the fetch filled a whole page — older reports may exist. */
  get mayHaveMore() {
    return this.reports.length >= REPORTS_PAGE_LIMIT;
  }

  /**
   * Stale-while-revalidate, scoped per owner: the first load populates the
   * catalog; later calls (each page construction) serve the cached data
   * instantly (isLoading stays false once loaded) while refetching in the
   * same task, so reports created/edited in the builder appear on return
   * without a full reload. A failed revalidation keeps the stale data
   * silently. An owner change (re-login without app teardown) drops the
   * previous admin's cache first; restartable so a new owner's load cancels
   * an in-flight one instead of being dropped.
   */
  load = restartableTask(async () => {
    const ownerId = this.sessionContext.user?.id ?? null;
    if (this.ownerForLoad !== ownerId) {
      this.reports = [];
      this.companies = [];
      this.loaded = false;
      this.companiesLoaded = false;
      this.loadFailed = false;
      this.ownerForLoad = ownerId;
    }
    const wasLoaded = this.loaded;
    try {
      // Reports revalidate on every page entry (builder saves must show up);
      // the paginated companies catalog is fetched once and reused.
      const [reports, companies] = await Promise.all([
        this.#fetchReports(),
        this.companiesLoaded ? Promise.resolve(null) : this.#fetchCompanies(),
      ]);
      // Discard responses started for a different owner. Compare the LIVE
      // session id — ownerForLoad only changes via another load.perform(),
      // so it can't detect a re-login that happened with no competing load.
      if (!this.#ownerIsCurrent(ownerId)) return;
      this.reports = reports;
      if (companies) {
        this.companies = companies;
        this.companiesLoaded = true;
      }
      this.loadFailed = false;
      this.loaded = true;
    } catch (error) {
      console.error('Failed to load the reports catalog:', error);
      if (!wasLoaded && this.#ownerIsCurrent(ownerId)) this.loadFailed = true;
    }
  });

  #ownerIsCurrent(ownerId: string | null | undefined) {
    return (
      this.ownerForLoad === ownerId && (this.sessionContext.user?.id ?? null) === (ownerId ?? null)
    );
  }

  refresh = dropTask(async () => {
    this.loaded = false;
    this.companiesLoaded = false;
    await this.load.perform();
  });

  companyNameFor = (report: Report) => {
    const companyId = companyIdFor(report);
    if (!companyId) return undefined;
    return this.companyNamesById.get(companyId);
  };

  /**
   * Set/clear metadata.archivedAt; resolves true on success. Only the
   * camelCase `metadata` field is written back onto the record (the PATCH
   * response echoes what we sent); assigning raw response attributes would
   * hit the SchemaRecord set trap with dasherized keys. Failures log, leave
   * the record unchanged, and resolve false so callers can surface them.
   */
  archiveReport = async (report: Report) => {
    return this.#updateArchivedAt(report, new Date().toISOString());
  };

  unarchiveReport = async (report: Report) => {
    return this.#updateArchivedAt(report, undefined);
  };

  async #updateArchivedAt(report: Report, archivedAt: string | undefined) {
    if (!report?.id) return false;
    try {
      // Serialize behind any in-flight revalidation: its GET captured
      // pre-mutation metadata and would materialize AFTER our PATCH,
      // reverting the row to its old rail.
      if (this.load.isRunning) {
        await this.load.last?.catch(() => {});
      }
      const metadata = { ...(report.metadata || {}) };
      if (archivedAt) {
        metadata.archivedAt = archivedAt;
      } else {
        delete metadata['archivedAt'];
      }
      const request = updateReportRequest(report.id, { metadata });
      request.cacheOptions = { [SkipCache]: true };
      await this.store.request(request);
      report.metadata = metadata;
      // Converge with server truth — also corrects any read that slipped in
      // between the wait above and the PATCH landing.
      void this.load.perform();
      return true;
    } catch (error) {
      console.error(`Failed to ${archivedAt ? 'archive' : 'unarchive'} report:`, error);
      return false;
    }
  }

  async #fetchReports() {
    // reload (NOT SkipCache): revalidation must hit the network — the
    // document cache's 5-minute soft TTL would otherwise serve the pre-
    // builder-save list — while keeping content.data materialized records.
    const { content } = (await this.store.request({
      ...query<Report>(
        'report',
        {
          'page[limit]': REPORTS_PAGE_LIMIT,
          'filter[report_type]': REPORT_TYPE_FILTER_VALUE,
        },
        { namespace: 'api/v2' }
      ),
      cacheOptions: { reload: true },
    })) as { content: { data: Report[] } };
    return content.data;
  }

  async #fetchCompanies() {
    // Paginate: a single 400 page silently dropped company labels/rail items
    // once an account exceeds it. MAX_PAGES bounds runaway growth.
    const PAGE = 400;
    const MAX_PAGES = 8;
    const all: CompanyOption[] = [];
    for (let page = 0; page < MAX_PAGES; page++) {
      const { content } = (await this.store.request({
        ...query<CompanyModel>('company', {
          'filter[is-active]': 1,
          sort: 'name',
          'page[limit]': PAGE,
          'page[offset]': page * PAGE,
        }),
        cacheOptions: { reload: true },
      })) as { content: { data: CompanyModel[] } };
      all.push(
        ...content.data.map((c) => ({
          key: String(c.id),
          label: c.name || `Company ${c.id}`,
        }))
      );
      if (content.data.length < PAGE) break;
    }
    return all;
  }
}
